/**
 * ConvictionSparkline — Reusable inline sparkline for conviction/confidence history.
 * Used on Themes, Credibility, Currency, Crypto Pulse, etc.
 *
 * Props:
 *   data: array of numeric values, or array of {value} objects, or JSON string
 *   stance: 'bullish' | 'bearish' | 'neutral' (colors the line)
 *   width, height: dimensions (default 60x18)
 */

const STANCE_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280' };

function normalize(input) {
  if (!input) return [];
  let arr = input;
  if (typeof input === 'string') { try { arr = JSON.parse(input); } catch { return []; } }
  if (!Array.isArray(arr)) return [];
  return arr.map(p => {
    if (typeof p === 'number') return p;
    if (p && typeof p === 'object') return typeof p.value === 'number' ? p.value : (typeof p.conviction === 'number' ? p.conviction : null);
    return null;
  }).filter(v => v !== null);
}

export default function ConvictionSparkline({ data, stance = 'neutral', width = 60, height = 18, accessor }) {
  let values = normalize(data);
  // Keep last 7 points for compact display
  if (values.length > 7) values = values.slice(-7);

  if (values.length < 2) {
    return <span className="inline-block text-[9px] font-mono text-korinth-muted/40" style={{ width, height }}>—</span>;
  }

  const color = STANCE_COLORS[stance] || '#6b7280';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(0.01, max - min);
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;

  const points = values.map((v, i) => ({
    x: i * stepX,
    y: height - ((v - min) / range) * (height - 2) - 1,
  }));

  const path = 'M ' + points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ');
  const lastPoint = points[points.length - 1];

  return (
    <svg width={width} height={height} className="inline-block" aria-label="conviction trajectory">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastPoint.x} cy={lastPoint.y} r="1.8" fill={color} />
    </svg>
  );
}

/**
 * Helper: compute trajectory delta from history.
 * Returns { delta, days, direction: 'up'|'down'|'stable' }
 */
export function computeTrajectory(historyJson) {
  try {
    const history = typeof historyJson === 'string' ? JSON.parse(historyJson) : historyJson;
    if (!Array.isArray(history) || history.length < 2) return null;
    const first = history[0];
    const last = history[history.length - 1];
    const delta = (last.value || 0) - (first.value || 0);
    const days = Math.max(1, Math.round((new Date(last.computed_at).getTime() - new Date(first.computed_at).getTime()) / 86400000));
    const direction = Math.abs(delta) < 0.2 ? 'stable' : (delta > 0 ? 'up' : 'down');
    return { delta: Math.round(delta * 10) / 10, days, direction };
  } catch { return null; }
}
